import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { ShoppingBag, Filter, ArrowLeft, ArrowUpRight, Palette, Headphones, BookOpen, Gift } from "lucide-react";
import { books } from "@/data/books";
import { eduMaterials } from "@/data/edu";
import BookCover from "@/components/BookCover";
import { useCart } from "@/hooks/useCart";
import { toast } from "sonner";

const iconMap = { palette: Palette, headphones: Headphones, book: BookOpen, gift: Gift };

const filters = [
  { id: "all", label: "Wszystko" },
  { id: "books", label: "Książki" },
  { id: "edu", label: "Pomoce" },
  { id: "digital", label: "Cyfrowe" },
];

export default function ShopPage() {
  const [filter, setFilter] = useState("all");
  const { addItem, openCart } = useCart();

  const products = useMemo(() => {
    const combined = [
      ...books.map((b) => ({ ...b, type: "book" })),
      ...eduMaterials.map((m) => ({ ...m, type: "edu" })),
    ];
    if (filter === "all") return combined;
    if (filter === "books") return combined.filter((p) => p.type === "book");
    if (filter === "edu") return combined.filter((p) => p.type === "edu");
    if (filter === "digital")
      return combined.filter((p) => p.kind === "digital" || (p.type === "book" && p.language === "EN"));
    return combined;
  }, [filter]);

  return (
    <div className="pt-32 md:pt-40 pb-20 min-h-screen relative" data-testid="shop-page">
      {/* Decorative aurora */}
      <div className="aurora" style={{ width: 520, height: 520, background: "#BAE6FD", top: "5%", left: "-8%" }} />
      <div className="aurora" style={{ width: 420, height: 420, background: "#FDE68A", top: "50%", right: "-8%" }} />

      <div className="max-w-7xl mx-auto px-6 md:px-10 relative">
        <Link
          to="/"
          data-testid="shop-back"
          className="inline-flex items-center gap-2 chip !bg-white/80 !border-white hover:bg-white"
        >
          <ArrowLeft size={14} /> Strona główna
        </Link>

        <div className="mt-8 md:mt-10 flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <div>
            <span className="chip">
              <span className="dot" /> Sklep
            </span>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
              className="font-display font-semibold text-[#051937] text-4xl sm:text-5xl md:text-6xl lg:text-7xl mt-6 tracking-tight leading-[1.02] max-w-4xl"
            >
              Wszystko, co Tonek i Duszek biorą <em className="not-italic text-[#60A5FA]">na wyprawę.</em>
            </motion.h1>
            <p className="mt-6 text-lg text-[#0b2a55]/75 max-w-2xl">
              Książki w twardej oprawie, kolorowanki, karty pracy, audiobook i pakiety prezentowe. Płatność zabezpieczona przez Stripe.
            </p>
          </div>
          <button
            onClick={openCart}
            data-testid="shop-cart"
            className="hidden md:inline-flex items-center gap-2 rounded-full bg-[#051937] hover:bg-[#0b2a55] text-white px-5 py-3 font-semibold"
          >
            <ShoppingBag size={16} /> Otwórz koszyk
          </button>
        </div>

        {/* Filters */}
        <div className="mt-12 flex flex-wrap items-center gap-3">
          <span className="text-xs uppercase tracking-[0.18em] font-bold text-[#0b2a55]/70 flex items-center gap-2">
            <Filter size={12} /> Filtruj
          </span>
          {filters.map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              data-testid={`filter-${f.id}`}
              className={`rounded-full px-4 py-2 text-xs uppercase tracking-[0.16em] font-bold border transition-all ${
                filter === f.id
                  ? "bg-[#051937] text-white border-[#051937]"
                  : "bg-white text-[#051937] border-[#BAE6FD] hover:border-[#84C5F4]"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Grid */}
        <AnimatePresence mode="wait">
          <motion.ul
            key={filter}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="mt-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-8"
          >
            {products.map((p, i) => {
              if (p.type === "book") {
                return (
                  <motion.li
                    key={p.slug}
                    initial={{ opacity: 0, y: 40 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: i * 0.04, ease: [0.16, 1, 0.3, 1] }}
                    whileHover={{ y: -6 }}
                    className="group"
                    data-testid={`shop-item-${p.slug}`}
                  >
                    <Link to={`/ksiazka/${p.slug}`}>
                      <BookCover book={p} />
                    </Link>
                    <div className="mt-4">
                      <div className="flex items-start justify-between gap-2">
                        <h3 className="font-display font-semibold text-[#051937] text-lg leading-tight">
                          {p.title}
                        </h3>
                        <span className="font-display font-semibold text-[#051937]">{p.price_pln} zł</span>
                      </div>
                      <p className="mt-1 text-xs uppercase tracking-[0.14em] font-bold text-[#0b2a55]/60">
                        {p.subtitle}
                      </p>
                      <button
                        onClick={() => {
                          addItem(p.lookup_key);
                          toast.success(`${p.title} — do koszyka 📚`);
                        }}
                        data-testid={`shop-add-${p.slug}`}
                        className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-[#051937] hover:bg-[#0b2a55] text-white px-4 py-2 text-xs font-semibold"
                      >
                        <ShoppingBag size={14} /> Do koszyka
                      </button>
                    </div>
                  </motion.li>
                );
              }
              // Edu
              const Icon = iconMap[p.icon] || BookOpen;
              return (
                <motion.li
                  key={p.slug}
                  initial={{ opacity: 0, y: 40 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: i * 0.04, ease: [0.16, 1, 0.3, 1] }}
                  whileHover={{ y: -6, rotate: -0.5 }}
                  className="rounded-[2rem] p-6 relative overflow-hidden aspect-[3/4] flex flex-col"
                  style={{ background: `linear-gradient(160deg, ${p.color}66 0%, ${p.color}22 100%)`, border: `1px solid ${p.accent}44` }}
                  data-testid={`shop-item-${p.slug}`}
                >
                  <div
                    className="w-14 h-14 rounded-2xl grid place-items-center text-white"
                    style={{ background: p.accent }}
                  >
                    <Icon size={26} strokeWidth={2.2} />
                  </div>
                  <div className="mt-4">
                    <span className="chip !bg-white/80 !border-white !text-[#051937] !py-1 !px-2 !text-[10px]">
                      Pomoc edukacyjna
                    </span>
                  </div>
                  <h3 className="mt-3 font-display font-semibold text-[#051937] text-xl leading-tight">
                    {p.title}
                  </h3>
                  <p className="mt-1 text-xs uppercase tracking-[0.14em] font-bold text-[#0b2a55]/60">
                    {p.subtitle}
                  </p>
                  <div className="mt-auto pt-4 flex items-center justify-between">
                    <span className="font-display font-semibold text-[#051937] text-xl">
                      {p.price_pln} zł
                    </span>
                    <button
                      onClick={() => {
                        addItem(p.lookup_key);
                        toast.success(`${p.title} — w koszyku ✨`);
                      }}
                      data-testid={`shop-add-${p.slug}`}
                      className="inline-flex items-center gap-1.5 rounded-full bg-[#051937] hover:bg-[#0b2a55] text-white px-4 py-2 text-xs font-semibold"
                    >
                      <ShoppingBag size={14} /> Kup
                    </button>
                  </div>
                </motion.li>
              );
            })}
          </motion.ul>
        </AnimatePresence>

        <div className="mt-16 text-center">
          <Link
            to="/kolorowanki"
            data-testid="shop-free"
            className="inline-flex items-center gap-2 text-sm font-semibold text-[#051937] animated-underline"
          >
            Zobacz darmowe pobrania <ArrowUpRight size={14} />
          </Link>
        </div>
      </div>
    </div>
  );
}
